import { inject, Pipe, type PipeTransform } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import {
	activeLocale,
	formatCurrency,
	formatDate,
	formatDateTimeNumeric,
	formatNumber,
	formatTime,
} from 'app/core/utils/locale';

/**
 * Locale-aware replacements for Angular's currency/number/date pipes: they format with the active
 * Transloco language (es-ES, en-GB, zh-CN) instead of the fixed LOCALE_ID, and follow a language
 * change. Impure on purpose (the language is not an input); the Intl formatters are cached, so a
 * change detection pass only formats the value again.
 */
abstract class LocaleFormatPipe {
	private transloco = inject(TranslocoService);

	protected locale(): string {
		return activeLocale(this.transloco);
	}
}

/** `{{ 12.5 | appCurrency }}` -> '12,50 €' (es) / '€12.50' (en). Args: digitsInfo ('1.2-2'), currency ('EUR'). */
@Pipe({ name: 'appCurrency', pure: false })
export class AppCurrencyPipe extends LocaleFormatPipe implements PipeTransform {
	transform(value: unknown, digitsInfo = '1.2-2', currency = 'EUR'): string {
		return formatCurrency(value, this.locale(), currency, digitsInfo);
	}
}

/** `{{ 1234.5 | appNumber }}` -> '1234,5' (es) / '1,234.5' (en). Arg: Angular digitsInfo ('1.0-3'). */
@Pipe({ name: 'appNumber', pure: false })
export class AppNumberPipe extends LocaleFormatPipe implements PipeTransform {
	transform(value: unknown, digitsInfo?: string): string {
		return formatNumber(value, this.locale(), digitsInfo);
	}
}

/** `{{ date | appDate: 'datetime' }}`. 'date' (default) short date, 'time' hh:mm, 'datetime' dd/MM/yyyy hh:mm. */
@Pipe({ name: 'appDate', pure: false })
export class AppDatePipe extends LocaleFormatPipe implements PipeTransform {
	transform(value: string | number | Date | null | undefined, style: 'date' | 'time' | 'datetime' = 'date'): string {
		const locale = this.locale();
		if (style === 'time') return formatTime(value, locale);
		if (style === 'datetime') return formatDateTimeNumeric(value, locale);
		return formatDate(value, locale);
	}
}

export const LOCALE_FORMAT_PIPES = [AppCurrencyPipe, AppNumberPipe, AppDatePipe] as const;
